import React, { FC } from 'react';
import { FormikHelpers, FormikProps } from 'formik';
import { observer } from 'mobx-react';

import {
    AppSection,
    AppSectionHeader,
    InputText,
    Form,
    AppFooter,
    Button,
    ButtonPrimary,
    ActionList,
    ButtonOutlinePrimary,
    useSnackbar
} from '../../../../components-v2/shared';

import useStore from '../../../../store/useStore';
import DefaultRoutes from '../../../../routes/DefaultRoutes';
import { Crm } from '../../../../types/Crm';
import { crmConfigTypes, unsavedChangesContainerSelectorDefault } from '../../../../constants';
// import { JSONWithRuleset, RuleEditor } from '../../../RuleEditor/RuleEditor';
// import { CrmRuleConditionSubjects, CrmRulePredicatePicklists } from '../helpers/FormHelper';
// import { RuleAction } from '../../../../types';
export interface ExacareFormProps {
    initialValues: any;
    onSubmit: (values: Crm, actions: FormikHelpers<Crm>, crmType: string) => Promise<void>;
    onValidate: (values: Crm, crmType: string) => Promise<void>;
    enableReinitialize?: boolean;
}

interface ExacareCrm extends Crm {
    configuration: {
        client_id: string;
        client_secret: string;
        organization_id: string;
        facility_id: string;
    };
}


export const ExacareForm: FC<ExacareFormProps> = observer(({ onSubmit, onValidate, ...props }) => {
    const { store } = useStore();
    const { router } = store;
    const { companyId } = router.params;
    const { addSnackbar } = useSnackbar();

    const handleCancel = () => router.goTo(DefaultRoutes.CrmSelectType, { companyId }, store);

    const handleValidate = async (formikProps: FormikProps<ExacareCrm>) => {
        await onValidate(formikProps.values, crmConfigTypes.exacare);
    };

    const handleSubmit = async (values: ExacareCrm, actions: FormikHelpers<ExacareCrm>) => {
        const { client_id, client_secret, organization_id, facility_id } = values.configuration;
        if (!facility_id || !client_id || !client_secret || !organization_id) {
            addSnackbar(`Please provide values to all fields.`, { variant: 'error' });
            return
        }
        await onSubmit(values, actions, crmConfigTypes.exacare);
    };

    return (
        <Form
            {...props}
            onSubmit={handleSubmit}
            confirmUnsavedChanges
            unsavedChangesConfig={{
                containerQuerySelectorAll: unsavedChangesContainerSelectorDefault
            }}
        >
            {(formikProps: FormikProps<ExacareCrm>) => {
                return (
                    <>
                        <AppSection className="field-group-content">
                            <div className="field-group-col">
                                <AppSectionHeader title="CRM Configuration: Exacare" />
                                <InputText
                                    name="name"
                                    label="Name"
                                    helperText="Unique name for this CRM Configuration"
                                    formikProps={formikProps}
                                    inputProps={{ maxLength: 64 }}
                                />
                            </div>
                            <div className="field-group-col">
                                <AppSectionHeader
                                    title="Connection Settings"
                                    subtitle="Contact Exacare support for your exact connection settings"
                                />
                                <InputText
                                    name="configuration.client_id"
                                    label="Client ID"
                                    helperText="Exacare Client ID"
                                    formikProps={formikProps}
                                    inputProps={{ maxLength: 255 }}
                                />
                                <InputText
                                    name="configuration.client_secret"
                                    label="Client Secret"
                                    helperText="Exacare Client Secret"
                                    formikProps={formikProps}
                                    inputProps={{ maxLength: 255 }}
                                />
                                <InputText
                                    name="configuration.organization_id"
                                    label="Organization ID"
                                    helperText="Exacare Organization ID"
                                    formikProps={formikProps}
                                    inputProps={{ maxLength: 255 }}
                                />
                                <InputText
                                    name="configuration.facility_id"
                                    label="Facility ID"
                                    helperText="Exacare Facility ID"
                                    formikProps={formikProps}
                                    inputProps={{ maxLength: 255 }}
                                />
                            </div>
                        </AppSection>

                        <AppFooter sticky={true}>
                            <ActionList position="end">
                                {formikProps.dirty && (
                                    <Button onClick={handleCancel} data-lc-trigger-unsaved-changes={true}>
                                        Cancel
                                    </Button>
                                )}
                                <ButtonOutlinePrimary onClick={() => handleValidate(formikProps)}>
                                    Validate
                                </ButtonOutlinePrimary>
                                <ButtonPrimary type="submit" disabled={!formikProps.dirty || formikProps.isSubmitting}>
                                    {formikProps.isSubmitting ? 'Saving...' : 'Save'}
                                </ButtonPrimary>
                            </ActionList>
                        </AppFooter>
                    </>
                );
            }}
        </Form>
    );
});
